@if (caseRecord(); as record) {
  <div
    class="bn-case-detail flex flex-col gap-4"
    [attr.aria-busy]="caseSaving()"
  >
    <div
      class="bn-case-detail__headline flex items-start justify-between gap-3"
    >
      <div class="flex-1 flex flex-col gap-2">
        @if (editingDescription()) {
          <textarea
            class="bn-case-detail__description-input"
            pTextarea
            rows="3"
            maxlength="250"
            aria-label="Case description"
            [disabled]="caseSaving()"
            [ngModel]="draftDescription()"
            (ngModelChange)="draftDescription.set($event)"
          ></textarea>
          @if (descriptionError(); as error) {
            <p role="alert" class="text-sm m-0">{{ error }}</p>
          }
          <div class="flex gap-2">
            <button
              type="button"
              [disabled]="caseSaving() || liveReadOnly()"
              (click)="saveDescription()"
            >
              {{ descriptionSaving() ? 'Saving…' : 'Save' }}
            </button>
            <button
              type="button"
              [disabled]="caseSaving()"
              (click)="cancelDescription()"
            >
              Cancel
            </button>
          </div>
        } @else {
          <div class="flex items-start gap-2">
            <span class="bn-case-detail__description">{{
              record.description
            }}</span>
            @if (!liveReadOnly() && !isClosedStatus(record.status)) {
              <button
                type="button"
                [disabled]="caseSaving()"
                aria-label="Edit case description"
                title="Edit description"
                (click)="startEditDescription()"
              >
                <i class="pi pi-pencil" aria-hidden="true"></i>
              </button>
            }
          </div>
        }
      </div>

      <div class="bn-case-detail__status">
        <button
          #statusTrigger
          type="button"
          class="bn-case-detail__status-pill"
          [attr.data-status]="record.status"
          [attr.aria-expanded]="
            isClosedStatus(record.status) ? null : statusMenuOpen()
          "
          [attr.aria-haspopup]="isClosedStatus(record.status) ? null : 'menu'"
          [disabled]="
            liveReadOnly() || caseSaving() || isClosedStatus(record.status)
          "
          [title]="
            liveReadOnly()
              ? 'Care Plan edit access is required to change status.'
              : ''
          "
          (click)="toggleStatusMenu($event)"
        >
          {{ store().statusLabel(record.status) }}
          @if (statusSaving() && !isClosedStatus(record.status)) {
            <i class="pi pi-spinner pi-spin" aria-hidden="true"></i>
          } @else if (!isClosedStatus(record.status)) {
            <i class="pi pi-chevron-down" aria-hidden="true"></i>
          }
        </button>

        @if (statusMenuOpen()) {
          <div class="bn-case-detail__status-menu" role="menu">
            @for (option of statusOptions(); track option.value) {
              <button
                #statusMenuItem
                type="button"
                class="bn-case-detail__status-option"
                role="menuitem"
                [disabled]="caseSaving()"
                (click)="onStatusChange(option.value)"
                (keydown)="onStatusMenuKeydown($event, $index)"
              >
                {{ option.label }}
              </button>
            }
            @if (canClose()) {
              <button
                #statusMenuItem
                type="button"
                class="bn-case-detail__status-option bn-case-detail__status-option--close"
                role="menuitem"
                [disabled]="caseSaving()"
                (click)="openCloseDialog()"
                (keydown)="onStatusMenuKeydown($event, statusOptions().length)"
              >
                Close the case…
              </button>
            }
            @if (store().isLive() && store().caseStatusLoading()) {
              <div
                role="status"
                aria-label="Loading statuses"
                data-testid="case-status-skeleton"
                class="flex flex-col gap-2 p-2"
              >
                <p-skeleton width="8rem" height="1.5rem" />
                <p-skeleton width="8rem" height="1.5rem" />
              </div>
            }
            @if (store().isLive() && store().caseStatusError()) {
              <p role="alert">Statuses could not be loaded.</p>
              <button
                #statusMenuItem
                type="button"
                class="bn-case-detail__status-option"
                role="menuitem"
                (click)="store().loadCaseStatuses(true)"
                (keydown)="onStatusMenuKeydown($event, 0)"
              >
                Retry
              </button>
            }
          </div>
        }
      </div>
    </div>

    @if (isClosedStatus(record.status)) {
      <div
        class="bn-case-detail__closed-banner flex items-center justify-between text-sm"
        role="status"
      >
        <span>{{
          store().statusLabel(record.status) +
            (record.status === 'closed'
              ? ' — ' + closedReasonLabel(record)
              : '')
        }}</span>
        <button
          type="button"
          class="bn-case-detail__reopen"
          [disabled]="liveReadOnly() || caseSaving()"
          [title]="
            liveReadOnly()
              ? 'Care Plan edit access is required to reopen this case.'
              : ''
          "
          (click)="reopen()"
        >
          {{ statusSaving() ? 'Reopening…' : 'Reopen' }}
        </button>
      </div>
    }

    <nav class="bn-case-detail__tabs flex items-center gap-4" role="tablist">
      <button
        type="button"
        class="bn-case-detail__tab"
        [class.bn-case-detail__tab--active]="activeTab() === 'plan'"
        role="tab"
        data-tab="plan"
        [id]="'case-plan-tab-' + caseId()"
        [attr.aria-controls]="'case-plan-panel-' + caseId()"
        [attr.aria-selected]="activeTab() === 'plan'"
        [tabIndex]="activeTab() === 'plan' ? 0 : -1"
        (click)="selectedTab.set('plan')"
        (keydown)="onTabKeydown($event)"
      >
        Plan
      </button>
      <button
        type="button"
        class="bn-case-detail__tab"
        role="tab"
        data-tab="outcomes"
        [id]="'case-outcomes-tab-' + caseId()"
        [attr.aria-controls]="'case-outcomes-panel-' + caseId()"
        [class.bn-case-detail__tab--active]="activeTab() === 'outcomes'"
        [class.bn-case-detail__tab--disabled]="!outcomesAvailable()"
        [disabled]="!outcomesAvailable()"
        [attr.aria-disabled]="!outcomesAvailable()"
        [attr.aria-selected]="activeTab() === 'outcomes'"
        [tabIndex]="activeTab() === 'outcomes' ? 0 : -1"
        (click)="selectedTab.set('outcomes')"
        (keydown)="onTabKeydown($event)"
      >
        Outcomes
      </button>
    </nav>

    @if (activeTab() === 'plan') {
      <div
        role="tabpanel"
        class="flex flex-col gap-4"
        [id]="'case-plan-panel-' + caseId()"
        [attr.aria-labelledby]="'case-plan-tab-' + caseId()"
        tabindex="0"
      >
        @if (
          store().goalsEnabled() &&
          (!store().isLive() ||
            (store().goalsLoaded() && !store().goalsError()))
        ) {
          <bn-case-goal-links
            [caseId]="caseId()"
            [store]="store()"
            (manageGoals)="openManageGoals()"
          />
        }

        <div class="bn-case-detail__plan-grid">
          <bn-options-section
            [caseId]="caseId()"
            [readonly]="record.status === 'closed' || liveReadOnly()"
          />

          <bn-actions-section
            [caseId]="caseId()"
            [readonly]="record.status === 'closed' || liveReadOnly()"
          />
        </div>
      </div>
    } @else {
      <div
        role="tabpanel"
        [id]="'case-outcomes-panel-' + caseId()"
        [attr.aria-labelledby]="'case-outcomes-tab-' + caseId()"
        tabindex="0"
      >
        <bn-outcomes-section [caseId]="caseId()" [carePlan]="store()" />
      </div>
    }
  </div>
}
